import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { 
  initializeE2EE, 
  encryptMessage, 
  decryptMessage, 
  getStoredKeys,
  cachePublicKey,
  getCachedPublicKey
} from '../lib/encryption';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EncryptionContext = createContext(null);

export const EncryptionProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [isInitialized, setIsInitialized] = useState(false);
  const [encryptionEnabled, setEncryptionEnabled] = useState(false);
  const [keys, setKeys] = useState(null);

  // Initialize E2EE when user logs in
  useEffect(() => {
    const init = async () => {
      if (user && token) {
        try {
          const userKeys = await initializeE2EE(user.id);
          setKeys(userKeys);
          
          // Upload public key to server
          await axios.put(`${API}/users/public-key`, {
            public_key: userKeys.publicKey
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          setIsInitialized(true);
          // Check localStorage for encryption preference
          const savedPref = localStorage.getItem('chatbuzzz_e2ee_enabled');
          setEncryptionEnabled(savedPref === 'true');
        } catch (error) {
          console.error('Failed to initialize E2EE:', error);
          setIsInitialized(false);
        }
      } else {
        setIsInitialized(false);
        setKeys(null);
      }
    };
    
    init();
  }, [user, token]);

  // Toggle encryption on/off
  const toggleEncryption = useCallback((enabled) => {
    setEncryptionEnabled(enabled);
    localStorage.setItem('chatbuzzz_e2ee_enabled', enabled.toString());
  }, []);

  // Get recipient's public key
  const getRecipientPublicKey = useCallback(async (userId) => {
    // Check cache first
    let publicKey = getCachedPublicKey(userId);
    
    if (!publicKey) {
      try {
        const response = await axios.get(`${API}/users/${userId}/public-key`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        publicKey = response.data.public_key;
        if (publicKey) {
          cachePublicKey(userId, publicKey);
        }
      } catch (error) {
        console.error('Failed to fetch public key:', error);
        return null;
      }
    }
    
    return publicKey;
  }, [token]);

  // Encrypt a message for a recipient
  const encrypt = useCallback(async (message, recipientUserId) => {
    if (!encryptionEnabled || !isInitialized) {
      return { content: message, encrypted: false };
    }
    
    const recipientPublicKey = await getRecipientPublicKey(recipientUserId);
    if (!recipientPublicKey) {
      // Recipient doesn't have E2EE set up, send unencrypted
      return { content: message, encrypted: false };
    }
    
    const encrypted = await encryptMessage(message, recipientPublicKey);
    if (encrypted.encrypted) {
      return {
        content: '[Encrypted message]',
        encrypted: true,
        encrypted_content: encrypted
      };
    }
    
    return { content: message, encrypted: false };
  }, [encryptionEnabled, isInitialized, getRecipientPublicKey]);

  // Decrypt a message
  const decrypt = useCallback(async (messageData) => {
    if (!messageData.encrypted || !keys) {
      return messageData.content;
    }
    
    try {
      const decrypted = await decryptMessage(messageData.encrypted_content, keys.privateKey);
      return decrypted;
    } catch (error) {
      console.error('Decryption failed:', error);
      return '[Unable to decrypt]';
    }
  }, [keys]);

  const value = {
    isInitialized,
    encryptionEnabled,
    toggleEncryption,
    encrypt,
    decrypt,
    hasKeys: !!keys
  };

  return (
    <EncryptionContext.Provider value={value}>
      {children}
    </EncryptionContext.Provider>
  );
};

export const useEncryption = () => {
  const context = useContext(EncryptionContext);
  if (!context) {
    throw new Error('useEncryption must be used within an EncryptionProvider');
  }
  return context;
};
